import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, TrendingUp, Users, Package, ArrowRight, Plus, Crown, ChevronRight, Check, DollarSign, Gift, Star, Coins, Clock, AlertCircle, ArrowUpCircle, X } from 'lucide-react';
import { userApi, settingsApi, packageApi } from '../../lib/api';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function UserOverview() {
  const [dashboard, setDashboard] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blcPrice, setBlcPrice] = useState(0.027);
  const [starProgress, setStarProgress] = useState(null);
  const [tierProgress, setTierProgress] = useState(null);
  const [transferringRoi, setTransferringRoi] = useState(false);
  const [activeBanners, setActiveBanners] = useState([]);
  const [roiInfo, setRoiInfo] = useState(null);
  const [showBannerPopup, setShowBannerPopup] = useState(false);
  const [bannerImage, setBannerImage] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const [dashRes, settingsRes, packagesRes, starRes, tierRes, bannersRes, roiRes] = await Promise.all([
        userApi.getDashboard(),
        settingsApi.get(),
        packageApi.getActive(),
        userApi.getStarProgress(),
        userApi.getTierProgress(),
        userApi.getActiveBanners(),
        userApi.getRoiInfo()
      ]);
      setDashboard(dashRes.data);
      setBlcPrice(settingsRes.data.settings?.blc_price || 0.027);
      setPackages(packagesRes.data.packages || []);
      setStarProgress(starRes.data);
      setTierProgress(tierRes.data);
      setActiveBanners(bannersRes.data.banners || []);
      setRoiInfo(roiRes.data);

      // Show dashboard banner popup once per session
      const banner = settingsRes.data.settings?.dashboard_banner;
      if (banner && !sessionStorage.getItem('banner_dismissed')) {
        setBannerImage(banner);
        setShowBannerPopup(true);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismissBanner = async (promoId) => {
    try {
      await userApi.dismissBanner(promoId);
      setActiveBanners(prev => prev.filter(b => b.id !== promoId));
    } catch (error) {
      console.error('Error dismissing banner:', error);
    }
  };

  const handleTransferRoiToCash = async () => {
    setTransferringRoi(true);
    try {
      await api.post('/user/transfer-roi-to-cash');
      toast.success('ROI transferred to Cash Wallet successfully!');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer not allowed today');
    } finally {
      setTransferringRoi(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  const usdToBLC = (usd) => (usd / blcPrice).toFixed(2);

  const depositWallet = dashboard?.deposit_wallet || 0;
  const cashWallet = dashboard?.cash_wallet || 0;
  const roiWallet = dashboard?.roi_wallet || 0;
  const commissionWallet = dashboard?.commission_wallet || 0;
  const salaryWallet = dashboard?.salary_wallet || 0;
  const promotionWallet = dashboard?.promotion_wallet || 0;

  const activeInvestment = dashboard?.investments?.find(inv => inv.is_active);

  const walletCards = [
    { label: 'Cash Wallet', value: cashWallet, description: 'Withdrawable & Stakeable', color: 'border-green-500', bgColor: 'from-green-500/20 to-green-500/5', icon: Wallet, canWithdraw: true },
    { label: 'Deposit Fund', value: depositWallet, description: 'For Staking Only', color: 'border-amber-500', bgColor: 'from-amber-500/20 to-amber-500/5', icon: DollarSign, isSpecial: true },
    { label: 'Daily ROI', value: roiWallet, description: `Next ROI: ${roiInfo?.roi_distribution_time || '00:00'} UTC`, color: 'border-blue-500', bgColor: 'from-blue-500/20 to-blue-500/5', icon: TrendingUp, showTransfer: roiWallet > 0, extraInfo: roiInfo?.next_withdrawal_date ? `Auto to Cash on ${roiInfo.next_withdrawal_date}th` : null },
    { label: 'Commission', value: commissionWallet, description: 'Instant to Cash Wallet', color: 'border-purple-500', bgColor: 'from-purple-500/20 to-purple-500/5', icon: Users },
    { label: 'Salary', value: salaryWallet, description: 'Moves to Cash on 1st monthly', color: 'border-pink-500', bgColor: 'from-pink-500/20 to-pink-500/5', icon: Star },
    { label: 'Promotion Reward', value: promotionWallet, description: 'Moves to Cash at promotion end', color: 'border-cyan-500', bgColor: 'from-cyan-500/20 to-cyan-500/5', icon: Gift },
  ];

  // Tier progress data
  const currentTier = tierProgress?.current_tier;
  const nextTier = tierProgress?.next_tier;
  const progress = tierProgress?.progress;
  const daysRemaining = tierProgress?.days_remaining || 0;
  const isMaxTier = tierProgress?.is_max_tier;

  return (
    <div className="space-y-8" data-testid="user-overview">
      {/* Dashboard Banner Popup */}
      <AnimatePresence>
        {showBannerPopup && bannerImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
            onClick={() => {
              setShowBannerPopup(false);
              sessionStorage.setItem('banner_dismissed', 'true');
            }}
            data-testid="banner-popup-overlay"
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

            {/* Banner Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative w-full max-w-md sm:max-w-lg md:max-w-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => {
                  setShowBannerPopup(false);
                  sessionStorage.setItem('banner_dismissed', 'true');
                }}
                className="absolute -top-3 -right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-zinc-800 border border-white/20 text-white hover:bg-red-500 hover:border-red-500 transition-all shadow-lg"
                data-testid="close-banner-btn"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Banner Image */}
              <img
                src={bannerImage}
                alt="Announcement"
                className="w-full h-auto rounded-2xl shadow-2xl border border-white/10"
                data-testid="banner-popup-image"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Promotion Banners */}
      <AnimatePresence>
        {activeBanners.map((banner) => (
          <motion.div
            key={banner.id}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="relative rounded-xl overflow-hidden border border-amber-500/30"
          >
            <button
              onClick={() => handleDismissBanner(banner.id)}
              className="absolute top-2 right-2 z-10 p-1.5 bg-black/60 hover:bg-black/80 rounded-full text-white transition-colors"
              data-testid={`dismiss-banner-${banner.id}`}
            >
              <X className="w-4 h-4" />
            </button>
            <Link to="/dashboard/promotions">
              <img 
                src={banner.banner} 
                alt={banner.name} 
                className="w-full h-32 sm:h-40 object-cover cursor-pointer hover:opacity-90 transition-opacity"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-medium">{banner.name}</h3>
                    <p className="text-amber-400 text-sm font-bold">Reward: ${banner.reward_amount}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-zinc-400">Ends</p>
                    <p className="text-sm text-white">{new Date(banner.end_date).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* BLC Price Banner */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-4 bg-gradient-to-r from-amber-500/10 to-transparent border-amber-500/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Coins className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-zinc-400">BLC Live Price</p>
              <p className="text-2xl font-bold text-amber-400">${blcPrice.toFixed(4)}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-zinc-500">Total Portfolio Value</p>
            <p className="text-lg text-white font-medium">{usdToBLC(cashWallet + depositWallet + roiWallet + salaryWallet + promotionWallet)} BLC</p>
            <p className="text-xs text-zinc-400">≈ ${(cashWallet + depositWallet + roiWallet + salaryWallet + promotionWallet).toFixed(2)} USD</p>
          </div>
        </div>
      </motion.div>

      {/* Wallet Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {walletCards.map((wallet, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: idx * 0.08, type: "spring", stiffness: 100 }}
            whileHover={{ scale: 1.02, y: -4 }}
            className={`wallet-card-animated relative overflow-hidden rounded-xl p-4 sm:p-5 border-2 ${wallet.color} bg-gradient-to-br ${wallet.bgColor} backdrop-blur-sm cursor-pointer group`}
            data-testid={`wallet-card-${wallet.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            {/* Animated border glow effect */}
            <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-r ${wallet.bgColor} blur-xl`} />
            
            {/* Floating particles animation */}
            <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-white/20 animate-ping" style={{ animationDelay: `${idx * 0.2}s` }} />
            <div className="absolute bottom-4 left-4 w-1 h-1 rounded-full bg-white/10 animate-pulse" style={{ animationDelay: `${idx * 0.3}s` }} />
            
            {wallet.isSpecial && (
              <motion.div 
                initial={{ x: 100 }}
                animate={{ x: 0 }}
                className="absolute top-0 right-0 bg-amber-500/30 text-amber-300 text-[8px] px-2 py-0.5 rounded-bl font-semibold tracking-wider"
              >
                STAKING ONLY
              </motion.div>
            )}
            <div className="relative z-10 flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-zinc-400 uppercase tracking-wider font-medium">{wallet.label}</p>
                <motion.p 
                  className="text-xl sm:text-2xl text-amber-400 font-bold mt-1 truncate"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: idx * 0.1 + 0.2 }}
                >
                  {usdToBLC(wallet.value)} BLC
                </motion.p>
                <p className="text-xs text-zinc-400 mt-1">≈ ${wallet.value.toFixed(2)} USD</p>
                <p className="text-[10px] text-zinc-500 mt-2 line-clamp-1">{wallet.description}</p>
                {wallet.extraInfo && (
                  <p className="text-[9px] text-blue-400 mt-1 line-clamp-1">{wallet.extraInfo}</p>
                )}
              </div>
              <motion.div 
                className={`p-2 sm:p-3 rounded-xl bg-white/5 border ${wallet.color} group-hover:scale-110 transition-transform duration-300`}
                whileHover={{ rotate: 10 }}
              >
                <wallet.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white/80" />
              </motion.div>
            </div>
            {wallet.showTransfer && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Button onClick={handleTransferRoiToCash} disabled={transferringRoi} size="sm" className="mt-3 w-full text-xs bg-blue-500/20 hover:bg-blue-500/40 text-blue-400 border border-blue-500/30 transition-all duration-300">
                  {transferringRoi ? 'Transferring...' : 'Transfer to Cash'}
                </Button>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Team Stats */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <motion.div 
          initial={{ opacity: 0, x: -20 }} 
          animate={{ opacity: 1, x: 0 }} 
          whileHover={{ scale: 1.02 }}
          className="glass-card p-3 sm:p-4 border-2 border-purple-500/30 hover:border-purple-500/50 transition-all duration-300"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded-lg bg-purple-500/20">
              <Users className="w-3 h-3 sm:w-4 sm:h-4 text-purple-400" />
            </div>
            <p className="text-xs text-zinc-500">Total Team</p>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-white">{dashboard?.total_team || 0}</p>
          <p className="text-xs text-zinc-400">{dashboard?.direct_referrals || 0} direct referrals</p>
        </motion.div>
        <motion.div 
          initial={{ opacity: 0, x: 20 }} 
          animate={{ opacity: 1, x: 0 }} 
          whileHover={{ scale: 1.02 }}
          className="glass-card p-3 sm:p-4 border-2 border-green-500/30 hover:border-green-500/50 transition-all duration-300"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded-lg bg-green-500/20">
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
            </div>
            <p className="text-xs text-zinc-500">Daily ROI (Est.)</p>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-green-400">${(dashboard?.roi_today || 0).toFixed(2)}</p>
          <p className="text-xs text-zinc-400">{usdToBLC(dashboard?.roi_today || 0)} BLC</p>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: 'Deposit', path: '/dashboard/deposit', icon: Wallet, color: 'from-green-500/20 to-green-500/5', borderColor: 'border-green-500/30 hover:border-green-500/60' },
          { label: 'Withdraw', path: '/dashboard/withdraw', icon: ArrowRight, color: 'from-blue-500/20 to-blue-500/5', borderColor: 'border-blue-500/30 hover:border-blue-500/60' },
          { label: 'Stake', path: '/dashboard/packages', icon: Package, color: 'from-amber-500/20 to-amber-500/5', borderColor: 'border-amber-500/30 hover:border-amber-500/60' },
          { label: 'Transfer', path: '/dashboard/transfer', icon: ArrowRight, color: 'from-purple-500/20 to-purple-500/5', borderColor: 'border-purple-500/30 hover:border-purple-500/60' },
        ].map((action, idx) => (
          <motion.div
            key={idx}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <Link 
              to={action.path} 
              className={`glass-card p-3 sm:p-4 block border-2 ${action.borderColor} transition-all duration-300 bg-gradient-to-br ${action.color}`} 
              data-testid={`quick-${action.label.toLowerCase()}`}
            >
              <div className="p-2 rounded-lg bg-white/5 w-fit mb-2">
                <action.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <p className="text-sm text-white font-medium">{action.label}</p>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Active Package + Next Tier Progress */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg text-white font-medium">Active Participation</h2>
          <Link to="/dashboard/packages" className="text-amber-400 text-sm hover:underline">View Packages</Link>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Active Investment */}
          <div className="space-y-4">
            {activeInvestment ? (
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="package-card p-5">
                <div className="absolute top-0 right-0 p-2 bg-green-500/10 text-green-400 text-[10px] font-bold rounded-bl-lg">ACTIVE</div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-amber-900/20 flex items-center justify-center text-amber-400">
                    <Crown className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{activeInvestment.current_tier || activeInvestment.package_name}</p>
                    <p className="text-xs text-zinc-400">{activeInvestment.daily_roi}% Daily Reward</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Staked Amount</span>
                    <div className="text-right">
                      <span className="text-amber-400 font-bold">{usdToBLC(activeInvestment.amount)} BLC</span>
                      <span className="text-zinc-400 ml-2">(${activeInvestment.amount.toFixed(2)})</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Total Earned</span>
                    <div className="text-right">
                      <span className="text-green-400 font-bold">{usdToBLC(activeInvestment.total_earned)} BLC</span>
                      <span className="text-zinc-400 ml-2">(${activeInvestment.total_earned.toFixed(2)})</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Tier Days Remaining</span>
                    <span className="text-amber-400 font-bold">{daysRemaining} days</span>
                  </div>
                  <div className="mt-3">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${Math.max(5, 100 - (daysRemaining / (currentTier?.duration_days || 30) * 100))}%` }} />
                    </div>
                  </div>
                </div>
                
                {/* Warning if time is running out */}
                {daysRemaining <= 7 && daysRemaining > 0 && !isMaxTier && (
                  <div className="mt-3 p-2 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                    <div className="flex items-center gap-2 text-orange-400 text-xs">
                      <AlertCircle className="w-4 h-4" />
                      <span>{daysRemaining} days left! Complete requirements to upgrade or capital returns to wallet.</span>
                    </div>
                  </div>
                )}
              </motion.div>
            ) : (
              <Link to="/dashboard/packages" className="glass-card p-5 flex flex-col items-center justify-center text-center min-h-[200px] hover:border-amber-500/30 group">
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-zinc-400 group-hover:bg-amber-500 group-hover:text-black transition-colors mb-3">
                  <Plus className="w-6 h-6" />
                </div>
                <p className="text-white font-medium">Activate Your First Package</p>
                <p className="text-xs text-zinc-500 mt-1">Start at Bronze Tier and grow!</p>
              </Link>
            )}
          </div>

          {/* Next Tier Progress Panel */}
          {activeInvestment && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="glass-card p-5 border-amber-500/20">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ArrowUpCircle className="w-5 h-5 text-amber-400" />
                  <h3 className="text-white font-medium">Next Tier Progress</h3>
                </div>
                {nextTier ? (
                  <span className="text-xs text-amber-400 px-2 py-1 bg-amber-500/10 rounded">{nextTier.name}</span>
                ) : (
                  <span className="text-xs text-green-400 px-2 py-1 bg-green-500/10 rounded">MAX TIER</span>
                )}
              </div>

              {currentTier && (
                <p className="text-xs text-zinc-400 mb-3">
                  Current: <span className="text-green-400 font-medium">{currentTier.name}</span>
                  <span className="text-zinc-500 ml-2">({currentTier.daily_roi}% daily)</span>
                </p>
              )}

              {isMaxTier ? (
                <div className="text-center py-6">
                  <Crown className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                  <p className="text-white font-medium">Congratulations!</p>
                  <p className="text-xs text-zinc-400">You've reached the highest tier.</p>
                  <p className="text-xs text-green-400 mt-2">Earning {currentTier?.daily_roi}% daily</p>
                </div>
              ) : nextTier && progress ? (
                <div className="space-y-4">
                  {/* Referral Requirements */}
                  <div>
                    <p className="text-xs text-zinc-500 mb-2 font-medium">Team Requirements</p>
                    <div className="space-y-2">
                      {Object.entries(progress.referral_progress || {}).map(([key, data]) => (
                        <div key={key}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-zinc-400 capitalize">{key.replace('_', ' ').replace('level', 'Level ')}</span>
                            <span className={data.met ? 'text-green-400' : 'text-white'}>
                              {data.current}/{data.required}
                              {data.met && <Check className="w-3 h-3 inline ml-1" />}
                            </span>
                          </div>
                          <div className="progress-bar h-1.5">
                            <div 
                              className="progress-fill"
                              style={{ 
                                width: `${Math.min(100, (data.current / data.required) * 100)}%`,
                                background: data.met ? 'linear-gradient(90deg, #22c55e, #16a34a)' : undefined
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Business Volume Requirement */}
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-zinc-400">Business Volume</span>
                      <span className={progress.business_volume_met ? 'text-green-400' : 'text-white'}>
                        ${(progress.business_volume?.current || 0).toLocaleString()} / ${(progress.business_volume?.required || 0).toLocaleString()}
                        {progress.business_volume_met && <Check className="w-3 h-3 inline ml-1" />}
                      </span>
                    </div>
                    <div className="progress-bar h-2">
                      <div 
                        className="progress-fill"
                        style={{ 
                          width: `${Math.min(100, ((progress.business_volume?.current || 0) / (progress.business_volume?.required || 1)) * 100)}%`,
                          background: progress.business_volume_met ? 'linear-gradient(90deg, #22c55e, #16a34a)' : undefined
                        }}
                      />
                    </div>
                    
                    {/* Business volume by level breakdown */}
                    {progress.business_volume?.by_level && (
                      <div className="mt-2 grid grid-cols-5 gap-1 text-[9px]">
                        {Object.entries(progress.business_volume.by_level).map(([level, vol]) => (
                          <div key={level} className="text-center p-1 bg-white/5 rounded">
                            <p className="text-zinc-500">{level.replace('level', 'L')}</p>
                            <p className="text-zinc-300">${vol.toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Next Tier Benefits */}
                  <div className="pt-3 border-t border-white/10">
                    <p className="text-xs text-zinc-500 mb-2">Next Tier Benefits</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 rounded bg-black/30 text-center">
                        <p className="text-amber-400 font-bold text-lg">{nextTier.daily_roi}%</p>
                        <p className="text-[10px] text-zinc-500">Daily ROI</p>
                      </div>
                      <div className="p-2 rounded bg-black/30 text-center">
                        <p className="text-purple-400 font-bold text-lg">{nextTier.duration_days}</p>
                        <p className="text-[10px] text-zinc-500">Duration Days</p>
                      </div>
                    </div>
                  </div>

                  {/* Status Message */}
                  {progress.all_requirements_met ? (
                    <div className="p-2 bg-green-500/10 border border-green-500/20 rounded-lg text-center">
                      <p className="text-green-400 text-xs font-medium">🎉 All requirements met!</p>
                      <p className="text-[10px] text-zinc-400">Auto-upgrade will happen during next check</p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-[10px] text-zinc-500 justify-center">
                      <Clock className="w-3 h-3" />
                      <span>{daysRemaining} days to complete requirements</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6 text-zinc-500 text-sm">
                  Loading tier progress...
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>

      {/* Referral Promo */}
      <div className="glass-card p-6 bg-gradient-to-r from-amber-500/10 to-transparent border-amber-500/20">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="text-white font-medium text-lg mb-1">Grow Your Team</h3>
            <p className="text-sm text-zinc-400">Build your team to unlock higher tiers and better rewards!</p>
          </div>
          <Link to="/dashboard/referral" className="gold-btn px-6 py-3 rounded-lg flex items-center gap-2 whitespace-nowrap">
            View Referrals <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
